'use client'

/**
 * © 2026 Grupo Salamander SAPI de CV
 *
 * hyperseguros-web is not being redesigned (portal-redesign spec, "Scope")
 * but shares `renaissance`'s `Theme` (`app/layout.tsx`), so plan task 1.12's
 * switch of the portal's MUI breakpoints (480/768/992/1280 -> the redesign's
 * approved 0/600/900/1200/1536) would silently reflow every `sx={{ xs, sm,
 * md, lg, xl }}` responsive value and every `theme.breakpoints.up(...)` call
 * on this site too (e.g. `Nav/index.tsx`'s desktop/mobile split at `md`) — a
 * real layout change nobody reviewed here.
 *
 * D3 (plan-audit, 2026-09-23): "give the website its own breakpoint values
 * via its local usage, not by keeping the portal on the old scale." This
 * re-provides the theme with the site's own, pre-redesign breakpoint values
 * layered on top of the shared palette/typography/glass from `Theme`, so
 * every `sx` responsive value and `useMediaQuery(theme.breakpoints...)` call
 * in this app keeps resolving exactly as it did before the portal's switch,
 * regardless of what `renaissance`'s `Theme` does with its own scale.
 */

import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles'
import type { Theme as MuiTheme } from '@mui/material/styles'
import type React from 'react'
import { useMemo } from 'react'

/** Identical to the portal's own pre-1.12 (legacy) breakpoint values. */
const SITE_BREAKPOINTS = {
  xs: 0,
  sm: 480,
  md: 768,
  lg: 992,
  xl: 1280,
} as const

export function LegacyBreakpointsProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const outer = useTheme() as MuiTheme
  const theme = useMemo(
    () =>
      // `createTheme(outer, { breakpoints: {...} })` (the two-argument,
      // "augment an existing theme" form) does NOT regenerate
      // `breakpoints.up`/`down`/etc.: those are already-bound functions on
      // `outer.breakpoints`, and the deep merge keeps them as-is since the
      // override only supplies `values` — verified against the installed
      // `@mui/material`, `.up('md')` still read the OLD width. Spreading
      // `outer`'s top-level keys into ONE options object, with `breakpoints`
      // replaced outright (not merged) in that same object, makes
      // `createTheme` treat it as fresh input and rebuild the breakpoint
      // functions from `SITE_BREAKPOINTS`.
      createTheme({ ...outer, breakpoints: { values: SITE_BREAKPOINTS } }),
    [outer]
  )
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>
}
